import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaTimes,
  FaBoxOpen,
  FaBarcode,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaToggleOn,
  FaSearch,
  FaSpinner,
  FaFileImport,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import apiRequest from "../services/api";

const ProductManagement = () => {
  // Data
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Search & Pagination
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(15);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const PAGE_SIZE_OPTIONS = [15, 20, 50, 100];

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [hasLink, setHasLink] = useState(false);
  // Form
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    sku: "",
    barcode: "",
    description: "",
    sellingPrice: 0.0,
    wholesalePrice: 0.0,
    purchasePrice: 0.0,
    exciseRate: 0.0,
    hsnCode: "",
    hasExpiryDate: false,
    taxable: false,
    status: "ACTIVE",
    type: "GOODS",
    categoryId: "",
    unitId: "",
  });

  // 1. Fetch Products (paginated)
  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const endpoint = `products?keyword=${encodeURIComponent(
        keyword.trim()
      )}&page=${page}&size=${size}`;

      const resp = await apiRequest(endpoint);
      const content = resp.data?.content || [];

      setProducts(content);
      setTotalPages(resp.data?.totalPages || 0);
      setTotalElements(resp.data?.totalElements || 0);
    } catch (err) {
      setError(err?.message || "Failed to load products.");
      setProducts([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setIsLoading(false);
    }
  }, [keyword, page, size]);

  // Reset to first page when keyword or size changes
  useEffect(() => {
    setPage(0);
  }, [keyword, size]);

  // Fetch whenever page / keyword / size changes
  useEffect(() => {
    const parsed = JSON.parse(localStorage.getItem("user") || "{}");
    const userData = parsed?.data || {};
    setHasLink(userData.tenant?.externalSource || false);

    fetchProducts();
  }, [fetchProducts]);

  // 3. Load Lookups
  const loadRelationLookups = async () => {
    try {
      const [catResp, unitResp] = await Promise.all([
        apiRequest("products/categories"),
        apiRequest("products/units"),
      ]);

      setCategories(catResp.data || []);
      setUnits(unitResp.data || []);
    } catch (err) {
      // ignore
    }
  };

  // 4. Modal Activation
  const openModal = async (mode, product = null) => {
    setModalMode(mode);
    setSelectedProduct(product);
    setError("");

    await loadRelationLookups();

    if (product) {
      setFormData({
        id: product.id,
        name: product.name || "",
        sku: product.sku || "",
        barcode: product.barcode || "",
        description: product.description || "",
        sellingPrice: product.sellingPrice || 0.0,
        wholesalePrice: product.wholesalePrice || 0.0,
        purchasePrice: product.purchasePrice || 0.0,
        exciseRate: product.exciseRate || 0.0,
        hsnCode: product.hsnCode || "",
        hasExpiryDate: product.hasExpiryDate ?? false,
        taxable: product.taxable ?? false,
        status: product.status || "ACTIVE",
        type: product.type || "GOODS",
        categoryId: product.categoryId || "",
        unitId: product.unitId || "",
      });
    } else {
      setFormData({
        id: null,
        name: "",
        sku: "",
        barcode: "",
        description: "",
        sellingPrice: 0.0,
        wholesalePrice: 0.0,
        purchasePrice: 0.0,
        exciseRate: 0.0,
        hsnCode: "",
        hasExpiryDate: false,
        taxable: false,
        status: "ACTIVE",
        type: "GOODS",
        categoryId: "",
        unitId: "",
      });
    }

    setIsModalOpen(true);
  };

  // 5. Save Product
  const handleSave = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name.trim()) {
      setError("Product Name attribute is explicitly required.");
      return;
    }

    if (!formData.categoryId) {
      setError(
        "Product must be mapped to a valid structural category framework."
      );
      return;
    }

    if (!formData.unitId) {
      setError("Product must be mapped to a valid UoM.");
      return;
    }

    const endpoint = "products";
    const method = modalMode === "create" ? "POST" : "PUT";

    try {
      await apiRequest(endpoint, {
        method,
        body: {
          ...formData,
          category: { id: formData.categoryId },
          unit: formData.unitId ? { id: formData.unitId } : null,
        },
      });

      setPage(0);
      fetchProducts();
      setIsModalOpen(false);
    } catch (err) {
      setError(err?.message || "Execution layer blocked resource mutation.");
    }
  };

  const importProductsSync = async () => {
    setIsImporting(true);
    setError("");

    try {
      const resp = await apiRequest("products/import", {
        method: "POST",
      });

      const imported = resp.imported ?? 0;

      setPage(0);
      await fetchProducts();

      alert(`${imported} product(s) imported successfully.`);
    } catch (err) {
      setError(err?.message || "Product import failed. Please try again.");
    } finally {
      setIsImporting(false);
    }
  };

  const handleImportProducts = async (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const fileName = file.name.toLowerCase();

    if (!fileName.endsWith(".xlsx") && !fileName.endsWith(".xls")) {
      setError("Please select a valid Excel file (.xlsx or .xls).");
      e.target.value = "";
      return;
    }

    setIsImporting(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);

      const resp = await apiRequest("products/import", {
        method: "POST",
        body: formData,
      });

      const imported = resp.imported ?? 0;

      setPage(0);
      await fetchProducts();

      alert(`${imported} product(s) imported successfully.`);
    } catch (err) {
      setError(
        err?.message ||
          "Product import failed. Please check the Excel file and try again."
      );
    } finally {
      setIsImporting(false);
      e.target.value = "";
    }
  };

  // 6. Delete Product
  const handleDelete = async (id, name) => {
    if (
      !window.confirm(
        `Drop product record specification "${name}" from inventory?`
      )
    )
      return;

    try {
      await apiRequest(`products/${id}`, { method: "DELETE" });

      setPage(0);
      fetchProducts();
    } catch (err) {
      setError(err?.message || "Purge execution pipeline blocked.");
    }
  };

  const inputClass =
    "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:opacity-60 transition dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-emerald-500/50";

  const selectClass =
    "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:opacity-60 transition appearance-none dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:focus:border-emerald-500/50";

  const labelClass =
    "text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400";

  const priceInputClass =
    "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono text-slate-800 text-right focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:opacity-60 transition dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:focus:border-emerald-500/50";

  return (
    <div className="min-h-full p-4 md:p-8 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100 relative pb-24">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white border border-slate-200 shadow-sm dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl p-6 rounded-2xl backdrop-blur-md">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
              <FaBoxOpen className="text-emerald-500" />
              Product Directory
            </h1>

            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Maintain items, prices, stock rules.
            </p>
          </div>

          <div className="flex items-center gap-2">
            {hasLink && (
              <button
                type="button"
                disabled={isImporting}
                onClick={() => importProductsSync()}
                className="flex items-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-900 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-xl transition-all shadow-lg dark:bg-white/10 dark:hover:bg-white/15"
              >
                {isImporting ? (
                  <FaSpinner className="animate-spin" />
                ) : (
                  <FaFileImport />
                )}

                {isImporting ? "Importing..." : "Import Products"}
              </button>
            )}
            <button
              type="button"
              onClick={() => openModal("create")}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-xl transition-all shadow-lg shadow-emerald-600/20"
            >
              <FaPlus />
              New Product
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none text-slate-400 dark:text-slate-500 text-xs">
            <FaSearch />
          </span>

          <input
            type="text"
            placeholder="Query by Name, SKU reference index code..."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-emerald-500/50"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-medium dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Main Table */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[11px] font-bold tracking-wider uppercase dark:border-white/10 dark:bg-slate-950/50 dark:text-slate-400">
                  <th className="px-6 py-4">Product</th>
                  <th className="px-6 py-4">SKU / Barcode</th>
                  <th className="px-6 py-4">Category / UoM</th>
                  <th className="px-6 py-4 text-right">Selling Price</th>
                  <th className="px-6 py-4 text-right">Wholesale Price</th>
                  <th className="px-6 py-4 text-center">Type</th>
                  <th className="px-6 py-4 text-center">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 text-slate-700 text-xs dark:divide-white/5 dark:text-slate-300">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-12 text-slate-500 font-sans italic dark:text-slate-500"
                    >
                      <div className="flex items-center justify-center gap-3">
                        <FaSpinner className="animate-spin text-emerald-500 text-base" />
                        <span>Synchronizing data...</span>
                      </div>
                    </td>
                  </tr>
                ) : products.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-12 text-slate-500 font-sans italic dark:text-slate-500"
                    >
                      No matching product found.
                    </td>
                  </tr>
                ) : (
                  products.map((prod) => (
                    <tr
                      key={prod.id}
                      className="hover:bg-slate-50 transition-colors dark:hover:bg-white/[0.02]"
                    >
                      {/* Product */}
                      <td className="px-6 py-4 font-semibold text-slate-900 dark:text-white">
                        <div>{prod.name}</div>

                        <div className="text-[10px] font-normal text-slate-500 max-w-xs truncate mt-0.5">
                          {prod.description}
                        </div>
                      </td>

                      {/* SKU / Barcode */}
                      <td className="px-6 py-4 font-semibold text-slate-900 dark:text-white">
                        <div>
                          <FaBarcode className="inline mr-1 opacity-40" />
                          {prod.sku}
                        </div>

                        <div className="text-[10px] font-normal text-slate-500 max-w-xs truncate mt-0.5">
                          {prod.barcode}
                        </div>
                      </td>

                      {/* Category / UoM */}
                      <td className="px-6 py-4">
                        <span className="text-slate-700 bg-slate-100 px-2 py-0.5 rounded text-[10px] border border-slate-200 mr-1.5 dark:text-slate-300 dark:bg-slate-950/60 dark:border-white/10">
                          {prod.categoryName}
                        </span>

                        <span className="text-slate-500 font-mono text-[10px] dark:text-slate-400">
                          {prod.unitName}
                        </span>
                      </td>

                      {/* Selling Price */}
                      <td className="px-6 py-4 text-right font-mono font-semibold text-emerald-700 dark:text-emerald-400">
                        {Number(prod.sellingPrice || 0).toFixed(2)}
                      </td>

                      {/* Wholesale Price */}
                      <td className="px-6 py-4 text-right font-mono font-semibold text-emerald-700 dark:text-emerald-400">
                        {Number(prod.wholesalePrice || 0).toFixed(2)}
                      </td>

                      {/* Type */}
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-medium tracking-wide ${
                            prod.type === "SERVICE"
                              ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
                              : "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400"
                          }`}
                        >
                          {prod.type}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-medium tracking-wide ${
                            prod.status === "ACTIVE"
                              ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
                              : "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400"
                          }`}
                        >
                          {prod.status}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openModal("view", prod)}
                            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-600 border border-slate-200 transition dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 dark:border-white/5"
                          >
                            <FaEye />
                          </button>

                          <button
                            onClick={() => openModal("edit", prod)}
                            className="p-2 bg-blue-50 hover:bg-blue-100 rounded-lg text-blue-600 border border-blue-200 transition dark:bg-blue-600/10 dark:hover:bg-blue-600/20 dark:text-blue-400 dark:border-blue-500/10"
                          >
                            <FaEdit />
                          </button>

                          <button
                            onClick={() => handleDelete(prod.id, prod.name)}
                            className="p-2 bg-red-50 hover:bg-red-100 rounded-lg text-red-600 border border-red-200 transition dark:bg-red-600/10 dark:hover:bg-red-600/20 dark:text-red-400 dark:border-red-500/10"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination + page-size selector */}
          {(totalPages > 1 || totalElements > 0) && (
            <div
              className="px-6 py-4 border-t flex flex-col sm:flex-row items-center justify-between gap-3 text-xs
              bg-slate-50 border-slate-200 text-slate-500
              dark:bg-slate-950/40 dark:border-white/5 dark:text-slate-400"
            >
              {/* Left: showing X of Y + size selector */}
              <div className="flex items-center gap-3 flex-wrap">
                <div>
                  Showing{" "}
                  <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                    {products.length}
                  </span>{" "}
                  of{" "}
                  <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                    {totalElements}
                  </span>{" "}
                  records.
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-slate-500 dark:text-slate-400">
                    Rows per page:
                  </span>
                  <select
                    value={size}
                    onChange={(e) => setSize(Number(e.target.value))}
                    disabled={isLoading}
                    className="rounded-lg px-2.5 py-1.5 text-xs font-semibold border appearance-none focus:outline-none focus:border-emerald-500/50
                      bg-white border-slate-200 text-slate-800
                      dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200
                      disabled:opacity-50"
                  >
                    {PAGE_SIZE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Right: prev / page indicator / next */}
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    disabled={page === 0 || isLoading}
                    onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                    className="p-2 rounded-md border transition disabled:opacity-30 disabled:pointer-events-none
                      bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100
                      dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    <FaChevronLeft />
                  </button>
                  <span className="font-mono text-slate-700 dark:text-slate-200">
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    disabled={page >= totalPages - 1 || isLoading}
                    onClick={() => setPage((prev) => prev + 1)}
                    className="p-2 rounded-md border transition disabled:opacity-30 disabled:pointer-events-none
                      bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100
                      dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    <FaChevronRight />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Drawer (modal) – unchanged */}
      <div
        className={`fixed inset-0 z-[2000] flex flex-col justify-end transition-opacity duration-300 ${
          isModalOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm dark:bg-slate-950/70"
          onClick={() => setIsModalOpen(false)}
        />

        {/* Drawer */}
        <div
          className={`relative w-full max-w-3xl mx-auto bg-white border border-slate-200 rounded-t-3xl p-6 md:p-8 max-h-[90vh] overflow-y-auto shadow-2xl transition-transform duration-300 transform space-y-6 dark:bg-slate-900 dark:border-white/10 ${
            isModalOpen ? "translate-y-0" : "translate-y-full"
          }`}
        >
          {/* Drawer Header */}
          <div className="flex justify-between items-start sticky top-0 bg-white dark:bg-slate-900 pb-2 border-b border-slate-200 dark:border-white/10 z-10 backdrop-blur-md">
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white capitalize">
                {modalMode} Inventory Item
              </h3>
            </div>

            <button
              onClick={() => setIsModalOpen(false)}
              className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 transition-colors dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
            >
              <FaTimes className="text-sm" />
            </button>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className={`${labelClass} flex items-center gap-1`}>
                  <FaToggleOn className="text-slate-400" />
                  Type
                </label>

                <select
                  disabled={modalMode === "view"}
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      type: e.target.value,
                    })
                  }
                  className={selectClass}
                >
                  <option value="GOODS">Goods</option>
                  <option value="SERVICE">Service</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>Item Name</label>

                <input
                  type="text"
                  required
                  placeholder="e.g. Paracetamol 500mg"
                  disabled={modalMode === "view"}
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      name: e.target.value,
                    })
                  }
                  className={inputClass}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>Barcode / QR</label>

                <input
                  type="text"
                  placeholder="barcode"
                  disabled={modalMode === "view"}
                  value={formData.barcode}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      barcode: e.target.value,
                    })
                  }
                  className={inputClass}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>SKU</label>

                <input
                  type="text"
                  placeholder="Auto-generated"
                  disabled={modalMode === "view" || modalMode === "create"}
                  value={formData.sku}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sku: e.target.value,
                    })
                  }
                  className={`${inputClass} font-mono`}
                />
              </div>
            </div>

            {/* Lookup Dropdowns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>Category</label>

                <select
                  required
                  disabled={modalMode === "view"}
                  value={formData.categoryId}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      categoryId: e.target.value,
                    })
                  }
                  className={selectClass}
                >
                  <option value="">-- Assign Category --</option>

                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>Unit of Measurement (UoM)</label>

                <select
                  required
                  disabled={modalMode === "view"}
                  value={formData.unitId}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      unitId: e.target.value,
                    })
                  }
                  className={selectClass}
                >
                  <option value="">-- Assign UoM --</option>

                  {units.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Financial Pricing */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200 dark:bg-slate-950/40 dark:border-white/10">
              <div className="flex flex-col gap-1.5">
                <label className={`${labelClass} flex items-center gap-1`}>
                  <FaMoneyBillWave className="text-blue-500 dark:text-blue-400" />
                  Purchase Price
                </label>

                <input
                  type="number"
                  step="0.01"
                  required
                  disabled={modalMode === "view"}
                  value={formData.purchasePrice}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      purchasePrice: parseFloat(e.target.value) || 0,
                    })
                  }
                  className={priceInputClass}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={`${labelClass} flex items-center gap-1`}>
                  <FaMoneyBillWave className="text-emerald-500 dark:text-emerald-400" />
                  Selling Price
                </label>

                <input
                  type="number"
                  step="0.01"
                  required
                  disabled={modalMode === "view"}
                  value={formData.sellingPrice}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sellingPrice: parseFloat(e.target.value) || 0,
                    })
                  }
                  className={priceInputClass}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={`${labelClass} flex items-center gap-1`}>
                  <FaMoneyBillWave className="text-purple-500 dark:text-purple-400" />
                  Wholesale Price
                </label>

                <input
                  type="number"
                  step="0.01"
                  disabled={modalMode === "view"}
                  value={formData.wholesalePrice}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      wholesalePrice: parseFloat(e.target.value) || 0,
                    })
                  }
                  className={priceInputClass}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className={`${labelClass} flex items-center gap-1`}>
                  <FaMoneyBillWave className="text-blue-500 dark:text-blue-400" />
                  Harmonization Code
                </label>

                <input
                  type="text"
                  disabled={modalMode === "view"}
                  value={formData.hsnCode}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      hsnCode: parseFloat(e.target.value) || "",
                    })
                  }
                  className={priceInputClass}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={`${labelClass} flex items-center gap-1`}>
                  <FaMoneyBillWave className="text-blue-500 dark:text-blue-400" />
                  Excise Rate (e.g 2%)
                </label>

                <input
                  type="number"
                  step="1"
                  required
                  disabled={modalMode === "view"}
                  value={formData.exciseRate}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      exciseRate: parseFloat(e.target.value) || 0,
                    })
                  }
                  className={priceInputClass}
                />
              </div>
            </div>

            {/* Operational Configuration */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200 cursor-pointer dark:bg-slate-950/40 dark:border-white/10">
                <input
                  type="checkbox"
                  disabled={modalMode === "view"}
                  checked={formData.taxable}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      taxable: e.target.checked,
                    })
                  }
                  className="rounded border-slate-300 bg-white text-emerald-500 focus:ring-0 w-4 h-4 dark:border-slate-800 dark:bg-slate-950"
                />

                <div>
                  <div className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1">
                    Taxable
                  </div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200 cursor-pointer dark:bg-slate-950/40 dark:border-white/10">
                <input
                  type="checkbox"
                  disabled={modalMode === "view"}
                  checked={formData.hasExpiryDate}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      hasExpiryDate: e.target.checked,
                    })
                  }
                  className="rounded border-slate-300 bg-white text-emerald-500 focus:ring-0 w-4 h-4 dark:border-slate-800 dark:bg-slate-950"
                />

                <div>
                  <div className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1">
                    Enforce Expiry
                  </div>
                </div>
              </label>

              <div className="flex flex-col gap-1.5">
                <label className={`${labelClass} flex items-center gap-1`}>
                  <FaToggleOn className="text-slate-400" />
                  Status
                </label>

                <select
                  disabled={modalMode === "view"}
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      status: e.target.value,
                    })
                  }
                  className={selectClass}
                >
                  <option value="ACTIVE">ACTIVE RUNNING</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div className="flex flex-col gap-1.5">
              <label className={labelClass}>Description</label>

              <textarea
                rows="2"
                placeholder="Enter composition info, cross-references, packaging variables..."
                disabled={modalMode === "view"}
                value={formData.description}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    description: e.target.value,
                  })
                }
                className={`${inputClass} resize-none`}
              />
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/10">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 text-xs font-semibold transition dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
              >
                Close
              </button>

              {modalMode !== "view" && (
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-md shadow-emerald-600/10 transition"
                >
                  {modalMode === "create" ? "Submit" : "Save Changes"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProductManagement;
